import React, { useEffect, useState } from 'react';
import { addBrand, getBrands, removeBrand, reorderBrands } from '../api/skipzyAdmin';

function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return typeof detail.message === 'string' ? detail.message : JSON.stringify(detail.message);
  return JSON.stringify(detail);
}

export default function BrandsSection() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function load() {
    try {
      setBrands((await getBrands()) || []);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load brands'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function add(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await addBrand(trimmed);
      setSuccess(`${trimmed} added. It shows in the app's brand picker now.`);
      setName('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not add brand'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(brand) {
    if (!window.confirm(`Remove ${brand.name}? It leaves the app's brand picker for everyone. Picks parents already saved are kept.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      setBrands((await removeBrand(brand.id)) || []);
      setSuccess(`${brand.name} removed.`);
    } catch (err) {
      setError(errorMessage(err, 'Could not remove brand'));
    } finally {
      setBusy(false);
    }
  }

  async function move(index, delta) {
    const target = index + delta;
    if (target < 0 || target >= brands.length) return;
    // Whole order goes up as one list so a failed call can't leave it half-moved.
    const ids = brands.map(b => b.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      setBrands((await reorderBrands(ids)) || []);
    } catch (err) {
      setError(errorMessage(err, 'Could not reorder brands'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-8">
      <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-1">Brands</h3>
      <p className="text-sm text-gray-500 mb-4">
        The brand chips parents pick from in the app's Trial Bag, shown in this order. Changes
        reach the app on its next refresh — no rebuild.
      </p>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {success && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{success}</div>}

      <form onSubmit={add} className="flex items-end gap-2 mb-4">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">New brand</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={80}
            placeholder="e.g. Zara Kids"
            className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="text-sm font-semibold bg-zoddle-green text-white px-3 py-2 rounded hover:opacity-90 disabled:opacity-50"
        >
          Add brand
        </button>
      </form>
      <div className="bg-white border border-gray-200 rounded overflow-hidden mb-3">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 w-16">Order</th>
              <th className="text-left px-4 py-2">Brand</th>
              <th className="text-left px-4 py-2">Last edited by</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-3 text-gray-400 text-sm">Loading…</td></tr>
            )}
            {!loading && brands.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-3 text-gray-400 text-sm">No brands — the app falls back to its built-in list.</td></tr>
            )}
            {brands.map((b, i) => (
              <tr key={b.id} className="border-t border-gray-100">
                <td className="px-4 py-3 text-gray-400">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0 || busy}
                    className="disabled:opacity-20 hover:text-zoddle-green px-1"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === brands.length - 1 || busy}
                    className="disabled:opacity-20 hover:text-zoddle-green px-1"
                  >
                    ↓
                  </button>
                </td>
                <td className="px-4 py-3 text-gray-800 font-semibold">{b.name}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{b.updated_by || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => remove(b)}
                    disabled={busy}
                    className="text-sm font-semibold border border-zoddle-pink text-zoddle-pink px-3 py-1.5 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
